'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { LinkButton, Typography } from 'components/Contentful'
import { Box, Button } from '@achieve/ascend'
import { _debounce, _get } from 'utils/shared'
import { HeroMedia } from 'components/HeroMedia'
import { useEffect, useRef, useState } from 'react'
import useIsSsr from 'hooks/useIsSsr'
import { cropText } from 'utils/shared'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { normalizeCategoryName } from 'utils/conversions'
import classNames from 'classnames'

function HeroSection({ content }) {
  const isSsr = useIsSsr()
  const { dynamicPathname, query } = useAppRouter()

  const memoizedContent = useMemoizedContentGetter(content, [
    'title',
    'member',
    'message',
    'image',
    'video',
    'button',
    'video_xs_portrait',
    'video_xs_landscape',
    'video_xs_square',
    'link',
  ])
  const [onViewport, setOnViewport] = useState(0)
  const isArticle = dynamicPathname === '/learn/[categoryname]'
  const isCategoryPage = isArticle || dynamicPathname === '/dollars-sense/[categoryname]'

  const {
    title,
    member,
    message,
    image,
    video,
    button,
    video_xs_portrait,
    video_xs_landscape,
    video_xs_square,
    link,
  } = memoizedContent

  const backgroundImage = _get(image, 'mediaContent', '')
  const backgroundImageMobile = _get(image, 'mobileMediaContent', '')
  const backgroundImageAlt = _get(image, 'mediaAltText', member?.textContent?.json)
  const [backgroundVideo, setBackgroundVideo] = useState(null)
  const backgroundVideoFull = _get(video, 'source', null)
  const backgroundPortrait = _get(video_xs_portrait, 'source', null)
  const backgroundLandscape = _get(video_xs_landscape, 'source', null)
  const backgroundSquare = _get(video_xs_square, 'source', null)
  const thumbnailVideoFull = _get(video, 'thumbnail', null)
  const thumbnailPortrait = _get(video_xs_portrait, 'thumbnail', null)
  const thumbnailLandscape = _get(video_xs_landscape, 'thumbnail', null)
  const thumbnailSquare = _get(video_xs_square, 'thumbnail', null)
  const [thumbnail, setThumbnail] = useState(thumbnailVideoFull)
  const videoRef = useRef(null)

  useEffect(() => {
    const viewPortFn = () =>
      window.innerWidth === window.innerHeight ? 0 : window.innerWidth > window.innerHeight ? 1 : 2
    setOnViewport(viewPortFn())
    const handleResize = _debounce(() => setOnViewport(viewPortFn()), 250, { maxWait: 250 })
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isSsr])

  useEffect(() => {
    let video = null
    let thumbnail = null
    if (onViewport === 0) {
      video = backgroundSquare
      thumbnail = thumbnailSquare
    } else if (onViewport === 1) {
      video = backgroundLandscape
      thumbnail = thumbnailLandscape
    } else {
      video = backgroundPortrait
      thumbnail = thumbnailPortrait
    }
    setBackgroundVideo(video ?? backgroundVideoFull)
    setThumbnail(thumbnail ?? thumbnailVideoFull)
  }, [
    onViewport,
    backgroundVideoFull,
    backgroundSquare,
    backgroundLandscape,
    backgroundPortrait,
    thumbnailVideoFull,
    thumbnailSquare,
    thumbnailLandscape,
    thumbnailPortrait,
  ])

  const handleEvent = (e) => {
    const target = e.target
    const parent = target.parentElement
    //open video dialog
    if (target.dataset.videoDialogOpen || parent.dataset.videoDialogOpen) {
      videoRef.current.pause()
    }
    //close video dialog
    if (target.dataset.videoDialogClose || parent.dataset.videoDialogClose) {
      videoRef.current.play()
    }
  }

  return (
    <Section
      className={
        'relative overflow-hidden h-[calc(100vh-72px)] flex flex-col lg:h-[528px] xl:items-center'
      }
      contain={false}
      data-testid="HeroSection_section"
    >
      <HeroMedia
        className={
          'h-full flex flex-row items-end lg:items-center lg:justify-end xl:min-w-[1440px] xl:max-w-[1440px]'
        }
        image={backgroundImage}
        mobileImage={backgroundImageMobile}
        video={backgroundVideoFull}
        mobileVideo={backgroundVideo}
        alt={backgroundImageAlt || 'hero-section-image'}
        videoRef={videoRef}
        thumbnail={thumbnail?.fields?.file?.url}
      >
        <Box
          className={
            'h-[calc(100vh-72px)] justify-center w-screen flex flex-col text-white shadow-[rgb(0_0_0/70%)_0_-150px_128px_-100px_inset] px-10 py-16 lg:min-w-[590px] lg:max-w-[590px] lg:shadow-none lg:mr-6 lg:p-0'
          }
        >
          <Box className={'max-w-xs *:mb-4 *:last:mt-8 *:last:mb-0 lg:max-w-[95%]'}>
            {title && (
              <Typography
                content={
                  isCategoryPage
                    ? `${normalizeCategoryName(query?.categoryname)} ${
                        isArticle ? 'Articles' : 'Blog Posts'
                      }`
                    : title?.textContent?.json
                }
                className={`font-bold! lg:font-medium! lg:bodyLg ${
                  backgroundVideo ? 'lg:headingMd' : 'lg:bodyLg'
                }`}
                component={isCategoryPage ? 'h1' : 'p'}
                data-testid="hero-section-title"
              />
            )}
            <Typography
              content={cropText(_get(member, 'textContent.json.content[0].content[0].value'), 30)}
              className="headingLg lg:displayXl font-bold!"
              component={isCategoryPage ? 'h2' : 'h1'}
              data-testid="hero-section-header"
            />
            {message && (
              <Typography
                className={classNames(
                  'lg:max-w-[487px]',
                  `font-medium! lg:font-light leading-relaxed! bodySm ${
                    backgroundVideo ? 'lg:bodyLg' : 'lg:headingMd'
                  }`
                )}
                content={message?.textContent}
                data-testid="hero-section-content"
                onClick={handleEvent}
              />
            )}
            {button && (
              <Button variant="contained" href={button.linkHref}>
                <Typography
                  className={''}
                  content={button?.linkText?.json?.content?.[0]}
                  variant="bodySm"
                  fontWeight="bold"
                />
              </Button>
            )}
            {link && (
              <Box>
                <LinkButton
                  content={link}
                  typographicOptions={{
                    variant: 'bodyLg',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </HeroMedia>
    </Section>
  )
}
export { HeroSection }
export default HeroSection
